import type { ButtonHTMLAttributes, ReactNode } from 'react';

type Variant = 'primary' | 'accent' | 'ghost' | 'subtle' | 'danger' | 'success';
type Size = 'sm' | 'md' | 'lg';

interface BtnProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: Variant;
  size?: Size;
  icon?: ReactNode;
  children?: ReactNode;
  fullWidth?: boolean;
}

export function Btn({
  variant = 'primary',
  size = 'md',
  icon,
  style,
  fullWidth,
  children,
  ...rest
}: BtnProps) {
  const h = size === 'sm' ? 32 : size === 'lg' ? 44 : 38;
  const px = size === 'sm' ? 12 : size === 'lg' ? 20 : 16;
  const fs = size === 'sm' ? 13 : size === 'lg' ? 15 : 14;

  const base: React.CSSProperties = {
    height: h,
    padding: `0 ${px}px`,
    borderRadius: h / 2,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    justifyContent: 'center',
    fontSize: fs,
    fontWeight: 600,
    border: 'none',
    cursor: rest.disabled ? 'not-allowed' : 'pointer',
    opacity: rest.disabled ? 0.5 : 1,
    transition: 'transform .08s, box-shadow .15s, background .15s',
    whiteSpace: 'nowrap',
    width: fullWidth ? '100%' : undefined,
  };

  const styles: Record<Variant, React.CSSProperties> = {
    primary: {
      background: '#0f1e3d',
      color: '#fff',
      boxShadow: '0 1px 0 rgba(255,255,255,.08) inset, 0 1px 2px rgba(15,30,61,.25)',
    },
    accent: {
      background: 'var(--accent)',
      color: '#3a2a00',
      boxShadow: '0 1px 0 rgba(255,255,255,.4) inset, 0 2px 6px rgba(244,185,58,.35)',
    },
    ghost: {
      background: 'transparent',
      color: '#0f1e3d',
      boxShadow: 'inset 0 0 0 1px rgba(15,30,61,.14)',
    },
    subtle: { background: 'rgba(15,30,61,.05)', color: '#0f1e3d' },
    danger: { background: '#d0463c', color: '#fff' },
    success: { background: '#1f9d55', color: '#fff' },
  };

  return (
    <button {...rest} style={{ ...base, ...styles[variant], ...style }}>
      {icon}
      {children}
    </button>
  );
}
